/// ///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2026 CONQUER INNOVATIONS SP. Z O.O. Global B.V. and its affiliate(s).
// All rights reserved.
//
// This software and other materials contain proprietary information
// controlled by CONQUER INNOVATIONS SP. Z O.O. and are protected by applicable copyright legislation.
// Any use and utilization of this software and other materials and
// disclosure to any third parties is conditional upon having a separate
// agreement with CONQUER INNOVATIONS SP. Z O.O. for the access, use, utilization or disclosure of this
// software. In the absence of such agreement, the use of the software is not
// allowed.
// The creator, author and originator of the code in the current file: Michał Pawłowski
//////////////////////////////////////////////////////////////////////////////////
<template>
  <b-modal
      v-model="isVisible"
      id="package-discount-form-modal"
      :title="isEditMode ? 'Edycja rabatu pakietowego' : 'Nowy rabat pakietowy'"
      title-class="font-18 fw-bold text-primary"
      size="lg"
      hide-footer
      @hidden="resetForm"
  >
    <form @submit.prevent="saveDiscount">
      <div class="row g-3">

        <div class="col-12">
          <h6 class="text-muted border-bottom pb-2 mb-3">Dane podstawowe</h6>
        </div>


        <div class="col-md-12">
          <label class="form-label required">Nazwa rabatu</label>
          <input type="text" class="form-control" v-model="form.name" required placeholder="np. Rabat lojalnościowy" />
        </div>

        <div v-if="wholesalerSelectBoxVisible" class="col-md-12">
          <label class="form-label">Hurtownik</label>
          <div class="input-group">
            <input
                :value="assignedWholesalerValue"
                type="text"
                class="form-control"
                readonly
                placeholder="Brak przypisanego hurtownika"
            >
            <button
                class="btn btn-outline-primary"
                type="button"
                @click="openWholesalerAssigningForm"
            >
              <i class="fas fa-exchange-alt me-1"></i> Zmień
            </button>
          </div>
        </div>

        <div class="col-12 mt-4">
          <h6 class="text-muted border-bottom pb-2 mb-3">Wartości i konfiguracja</h6>
        </div>

        <div class="col-md-6">
          <label class="form-label required">Typ rabatu</label>
          <select class="form-select" v-model="form.type" required>
            <option value="PERCENT">Procentowy (%)</option>
            <option value="QUOTA">Kwotowy (PLN)</option>
          </select>
        </div>

        <div class="col-md-6">
          <label class="form-label required">Wartość początkowa</label>
          <div class="input-group">
            <input type="number" step="0.01" class="form-control" v-model="form.initialValue" required min="0" />
            <span class="input-group-text">{{ form.type === 'PERCENT' ? '%' : 'PLN' }}</span>
          </div>
        </div>

        <div class="col-12 mt-3 d-flex align-items-center">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" v-model="form.separateForEveryOrderItem" id="separateSwitch">
            <label class="form-check-label" for="separateSwitch">
              Oddzielna wartość dla każdej pozycji
              <i class="fas fa-info-circle text-muted ms-1" title="Jeśli zaznaczone, każda pozycja w zamówieniu może mieć inną wartość tego rabatu. Jeśli odznaczone, zmiana wartości wpływa na wszystkie pozycje."></i>
            </label>
          </div>
        </div>

      </div>

      <hr class="my-4" />

      <div class="d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-secondary" @click="closeModal">Anuluj</button>
        <button type="submit" class="btn btn-primary px-4">
          <i class="fas fa-save me-1"></i> Zapisz
        </button>
      </div>
    </form>

    <SupplierListModal
        v-if="modals.showWholesaler"
        v-model:showModal="modals.showWholesaler"
        :multiselect="false"
        okTitle="Wybierz hurtownika"
        @onApply="handleWholesalerSelected"
        @onClose="modals.showWholesaler = false"
        :showMassActions="false"
        :showAddButton="false"
        :showCheckboxColumn="false"
        :visibleColumnsFilter="['id', 'centralId','nip','name']"
    />
  </b-modal>
</template>

<script>
import { BModal } from "bootstrap-vue-next";
import { usePackagesManagerModule } from "@/state/pinia/packagesManagerModule";
import { useLazyLoaderModule } from "@/state/pinia";
import { useCommonAppModule } from "@/state/pinia/commonAppModule";
import SupplierListModal from "@/views/DrugstoreCommonManager/SupplierListModal.vue";

export default {
  name: "PackageDiscountFormModal",
  components: { BModal, SupplierListModal },
  props: {
    showModal: { type: Boolean, required: true },
    initialData: { type: Object, default: null },
    orderGroupGuid: { type: String, required: true },
    wholesalerSelectBoxVisible: { type: Boolean, default: false },
    preselectedWholesalerGuid: { type: String, default: null }
  },
  emits: ["update:showModal", "onSaved", "onPackageDiscountSave"],
  data() {
    return {
      form: this.getEmptyForm(),
      currentAssignedWholesalerObject: null,
      modals: {
        showWholesaler: false
      }
    };
  },
  computed: {
    isVisible: {
      get() { return this.showModal; },
      set(val) { this.$emit("update:showModal", val); }
    },
    isEditMode() { return !!this.initialData; },
    packagesModule() { return usePackagesManagerModule(); },
    lazyLoaderModule() { return useLazyLoaderModule(); },
    commonAppModule() { return useCommonAppModule(); },

    assignedWholesalerValue() {
      if (this.currentAssignedWholesalerObject) {
        return `${this.currentAssignedWholesalerObject.id} - ${this.currentAssignedWholesalerObject.name}`;
      }
      return "Nie przypisano";
    }
  },
  watch: {
    initialData: {
      handler(newVal) {
        if (newVal) {
          this.form = { ...newVal };
          // Load wholesaler object if wholesalerGuid exists in initial data
          const wholesalerGuid = newVal.wholesalerGuid;
          if (wholesalerGuid) {
            this.loadWholesalerByGuid(wholesalerGuid);
          } else {
            this.currentAssignedWholesalerObject = null;
            // Try preselected if available
            if (this.preselectedWholesalerGuid) {
              this.loadWholesalerByGuid(this.preselectedWholesalerGuid);
            }
          }
        } else {
          this.form = this.getEmptyForm();
          this.currentAssignedWholesalerObject = null;
          // For create mode, try preselected wholesaler
          if (this.preselectedWholesalerGuid) {
            this.loadWholesalerByGuid(this.preselectedWholesalerGuid);
          }
        }
      },
      immediate: true
    },
    preselectedWholesalerGuid: {
      handler(newVal) {
        // If no initialData wholesaler and preselected changes, load it
        if (!this.initialData || !this.initialData.wholesalerGuid) {
          if (newVal) {
            this.loadWholesalerByGuid(newVal);
          } else {
            this.currentAssignedWholesalerObject = null;
          }
        }
      }
    }
  },
  methods: {
    getEmptyForm() {
      return {
        name: "",
        type: "PERCENT",
        initialValue: 0,
        separateForEveryOrderItem: false
      };
    },

    closeModal() {
      this.isVisible = false;
    },

    resetForm() {
      this.form = this.getEmptyForm();
      this.currentAssignedWholesalerObject = null;
    },

    loadWholesalerByGuid(guid) {
      if (!guid) return;
      this.commonAppModule.httpGetSupplierByGuid({ guid: guid }).then((response) => {
        this.currentAssignedWholesalerObject = response.data.payload;
      });
    },

    openWholesalerAssigningForm() {
      this.modals.showWholesaler = true;
    },

    handleWholesalerSelected(selection) {
      const wholesaler = Array.isArray(selection) ? selection[0] : selection;
      if (wholesaler) {
        this.currentAssignedWholesalerObject = wholesaler;
        this.modals.showWholesaler = false;
      }
    },

    saveDiscount() {
      const dto = {
        name: this.form.name,
        type: this.form.type,
        initialValue: this.form.initialValue,
        separateForEveryOrderItem: this.form.separateForEveryOrderItem,
        orderGroupGuid: this.orderGroupGuid,
        wholesalerGuid: this.wholesalerSelectBoxVisible && this.currentAssignedWholesalerObject
            ? this.currentAssignedWholesalerObject.guid
            : (this.preselectedWholesalerGuid || null)
      };

      if (this.isEditMode) {
        // EDYCJA
        this.lazyLoaderModule.setDisplayLazyLoader(true);
        console.log("Edycja rabatu pakietowego", dto);
        this.packagesModule.httpUpdatePackageDiscount({
          guid: this.initialData.guid,
          dto: dto
        }).then((response) => {
          if (response && response.status === 200) {
            this.$emit("onPackageDiscountSave",dto);
            this.closeModal();
          }
        });
      } else {
        // TWORZENIE
        this.lazyLoaderModule.setDisplayLazyLoader(true);
        console.log("Tworzenie rabatu pakietowego", dto);
        this.packagesModule.httpCreatePackageDiscount({
          dto: dto
        }).then((response) => {
          if (response && response.status === 200) {
            this.$emit("onPackageDiscountSave",dto);
            this.closeModal();
          }
        });
      }
    }
  }
};
</script>

<style scoped>
.required:after { content: " *"; color: red; }
</style>
